import apiClient from './apiClient';
import type { ApiResponse, MedicalConsultation } from '../types/api';

/** A signed addendum to a signed consultation (clinical-record-amendments R1-R4). */
export interface ClinicalAmendment {
    id: number;
    amendable_id: number;
    doctor_id: number;
    recorded_by?: number | null;
    reason: string;
    amendment_text: string;
    status: 'draft' | 'signed';
    lock_version: number;
    signed_by?: number | null;
    signed_at?: string | null;
    signature_method?: MedicalConsultation['signature_method'];
    created_at?: string;
}

export interface AmendmentPayload {
    reason: string;
    amendment_text: string;
}

export const clinicalAmendmentService = {
    async getByConsultation(consultationId: number): Promise<ClinicalAmendment[]> {
        const response = await apiClient.get<ApiResponse<ClinicalAmendment[]>>(`/consultations/${consultationId}/amendments`);
        return response.data.data;
    },

    /** Always created as a draft; signing is a separate, explicit call (R4). */
    async create(consultationId: number, data: AmendmentPayload): Promise<ClinicalAmendment> {
        const response = await apiClient.post<ApiResponse<ClinicalAmendment>>(`/consultations/${consultationId}/amendments`, data);
        return response.data.data;
    },

    /** `lock_version` is the version the edit is based on; 409 if someone saved first. */
    async update(id: number, data: Partial<AmendmentPayload> & { lock_version: number }): Promise<ClinicalAmendment> {
        const response = await apiClient.put<ApiResponse<ClinicalAmendment>>(`/clinical-amendments/${id}`, data);
        return response.data.data;
    },

    /** 409 when `lockVersion` is stale, same contract as consultation sign. */
    async sign(id: number, lockVersion: number): Promise<ClinicalAmendment> {
        const response = await apiClient.post<ApiResponse<ClinicalAmendment>>(`/clinical-amendments/${id}/sign`, {
            lock_version: lockVersion,
        });
        return response.data.data;
    },
};

export default clinicalAmendmentService;
